import { createTheme, ThemeProvider } from '@mui/material'
import { deDE as muiDeDE, enUS as muiEnUS } from '@mui/material/locale'
import { deDE as dgDeDE, enUS as dgEnUS } from '@mui/x-data-grid/locales'
import type {} from '@mui/x-data-grid/themeAugmentation'
import { deDE as dpDeDE, enUS as dpEnUS } from '@mui/x-date-pickers/locales'
import type {} from '@mui/material/themeCssVarsAugmentation'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod/v4-mini'
import { useGlobalSettingsColorScheme } from '@/queries/useGlobalSettingsColorScheme'

export const defaultThemePrimaryColorLight = import.meta.env
    .VITE_DEFAULT_THEME_PRIMARY_COLOR_LIGHT
export const defaultThemeSecondaryColorLight = import.meta.env
    .VITE_DEFAULT_THEME_SECONDARY_COLOR_LIGHT
export const defaultThemePrimaryColorDark = import.meta.env
    .VITE_DEFAULT_THEME_PRIMARY_COLOR_DARK
export const defaultThemeSecondaryColorDark = import.meta.env
    .VITE_DEFAULT_THEME_SECONDARY_COLOR_DARK

declare module '@mui/material/styles' {
    interface BreakpointOverrides {
        /**
         * Additional value for ultra-wide displayes.
         */
        xxl: true
    }
}

export const createAppTheme = (
    locale?: string,
    colorScheme?: Partial<
        Record<
            'dark' | 'light',
            Partial<Record<'primary' | 'secondary', string>>
        >
    >
) =>
    createTheme(
        {
            colorSchemes: {
                dark: {
                    palette: {
                        primary: {
                            main:
                                colorScheme?.dark?.primary ??
                                defaultThemePrimaryColorDark,
                        },
                        secondary: {
                            main:
                                colorScheme?.dark?.secondary ??
                                defaultThemeSecondaryColorDark,
                        },
                        TableCell: {
                            border: 'var(--mui-palette-divider)',
                        },
                    },
                },
                light: {
                    palette: {
                        TableCell: {
                            border: 'var(--mui-palette-divider)',
                        },
                    },
                },
            },
            cssVariables: { colorSchemeSelector: 'class' },
            components: {
                MuiAlert: {
                    styleOverrides: {
                        root: ({ theme }) => ({
                            backgroundColor:
                                theme.vars.palette.background.default,
                        }),
                    },
                },
                MuiCard: { defaultProps: { variant: 'outlined' } },
                MuiTextField: { defaultProps: { fullWidth: true } },
                MuiDataGrid: {
                    defaultProps: {
                        rowHeight: 58,
                        slotProps: {
                            loadingOverlay: {
                                variant: 'skeleton',
                                noRowsVariant: 'skeleton',
                            },
                        },
                    },
                },
                MuiChip: { defaultProps: { variant: 'outlined' } },
            },
            palette: {
                primary: {
                    main:
                        colorScheme?.light?.primary ??
                        defaultThemePrimaryColorLight,
                },
                secondary: {
                    main:
                        colorScheme?.light?.secondary ??
                        defaultThemeSecondaryColorLight,
                },
            },
            breakpoints: {
                values: {
                    // default values
                    xs: 0,
                    sm: 600,
                    md: 900,
                    lg: 1200,
                    xl: 1536,
                    // additional value
                    xxl: 1921,
                },
            },
        },
        ...(locale === 'de' ?
            [muiDeDE, dpDeDE, dgDeDE]
        :   [muiEnUS, dpEnUS, dgEnUS])
    )

/**
 * Provides the `ThemeProvider` needed for the application.
 * Automatically sets the current *i18n* locale as the locale in the `ThemeProvider`.
 */
export const I18NAwareThemeProvider: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    const { data: colorScheme } = useGlobalSettingsColorScheme()

    const [, { language }] = useTranslation()

    const themeWithLocale = React.useMemo(
        () => createAppTheme(language, colorScheme),
        [colorScheme, language]
    )

    React.useEffect(
        () =>
            void z.config(language === 'de' ? z.locales.de() : z.locales.en()),
        [language]
    )

    return <ThemeProvider theme={themeWithLocale}>{children}</ThemeProvider>
}
